'use client'

import { twMerge } from '@/lib/classname/twMerge'
import { twConfig } from '@/lib/tailwind/twConfig'
import type { VariantProps } from 'class-variance-authority'
import { cva } from 'class-variance-authority'
import { Fragment, type ImgHTMLAttributes, useState } from 'react'
import { useIsMounted } from 'usehooks-ts'
import type { CloudflarePictureCategorySizes } from '../cloudflare-picture-category/CloudflarePictureCategory'
import type { CloudflarePictureProductSizes } from '../cloudflare-picture-product/CloudflarePictureProduct.client'
import { Picture, PlaceholderPicture } from '../picture/Picture.client'
import { generatePictureSource } from '../picture/Picture.service'
import { useCloudflareConfig } from './CloudflareConfig.context'
import { CF_DEFAULT_QUALITY, getCloudflarePictureURLs } from './CloudflarePicture.service'

export type CloudflarePictureSizes =
  | CloudflarePictureCategorySizes
  | CloudflarePictureProductSizes
  | number

const cloudflarePictureCva = cva('', {
  variants: {
    mode: {
      cover: 'object-cover',
      contain: 'object-contain',
    },
  },
})

export type CloudflarePictureVariantProps = NonNullFields<VariantProps<typeof cloudflarePictureCva>>

export type CloudflarePictureDimension =
  | { mobile?: CloudflarePictureSizes; desktop?: CloudflarePictureSizes }
  | CloudflarePictureSizes

function normalize(val: CloudflarePictureDimension | undefined): number[] {
  if (!val) return []

  // Handle responsive objects like { mobile, desktop }
  if (typeof val === 'object') {
    const { mobile, desktop } = val
    const arr = [mobile, desktop].filter((v): v is number => typeof v === 'number' && !isNaN(v))
    if (arr.length) return arr
  }

  // Handle single numeric value
  if (typeof val === 'number' && !isNaN(val)) {
    return [val]
  }

  return []
}

export type CloudflarePictureProps = CloudflarePictureVariantProps & {
  src?: string
  alt?: string
  className?: string
  pictureClassName?: string
  shrink?: boolean
  quality?: number
  loadingStrategy?: ImgHTMLAttributes<HTMLImageElement>['loading']
  fetchPriority?: ImgHTMLAttributes<HTMLImageElement>['fetchPriority']
  isInternalPicture?: boolean
  width?: CloudflarePictureDimension
  height?: CloudflarePictureDimension
  'data-testid'?: string
  // `HTMLAttributes<HTMLPictureElement>` never had `width`/`height` keys to begin with (those
  // belong to `<img>`, not `<picture>`) - no StrictOmit needed, kept as a plain intersection
} & React.HTMLAttributes<HTMLPictureElement>

export function CloudflarePicture({
  src,
  alt,
  mode = 'cover',
  shrink = false,
  quality = CF_DEFAULT_QUALITY,
  loadingStrategy = 'lazy',
  fetchPriority = undefined,
  isInternalPicture = false,
  className,
  pictureClassName,
  width,
  height,
  id,
  'aria-label': ariaLabel,
  'data-testid': dataTestId,
  ...rest
}: CloudflarePictureProps) {
  const config = useCloudflareConfig()
  const [hasError, setHasError] = useState(false)
  const isMounted = useIsMounted()

  if (!src || (!src.startsWith('/') && !URL.canParse(src)) || hasError) {
    return (
      <PlaceholderPicture
        alt={alt}
        className={twMerge('size-full', className)}
        id={id}
        aria-label={ariaLabel}
        data-testid={dataTestId}
      />
    )
  }

  if (!config.enabled) {
    return (
      <Picture
        image={generatePictureSource({ mobileUrl: src, alt: alt ?? '' })}
        className={className}
        pictureClassName={pictureClassName}
        mode={mode}
        shrink={shrink}
        onError={() => setHasError(true)}
        id={id}
        aria-label={ariaLabel}
        data-testid={dataTestId}
      />
    )
  }

  const widthValues = normalize(width)
  const heightValues = normalize(height)

  if (!widthValues.length && !heightValues.length) {
    console.warn('[CloudflarePicture] No valid width or height found from props:', {
      width,
      height,
    })
  }

  const desktopWidth = twConfig.theme.screens['desktop']

  const data = getCloudflarePictureURLs({
    src,
    width: widthValues,
    height: heightValues,
    fit: mode,
    quality: Math.max(1, Math.min(quality, 100)),
    isInternalPicture,
    config,
    dpr: isMounted() ? window.devicePixelRatio : undefined,
  })

  const fallbackSrc = (() => {
    if (!data) return src

    const firstFormatUrls = Object.values(data)[0]
    if (!firstFormatUrls || firstFormatUrls.length === 0) return src

    const smallest = [...firstFormatUrls].sort((a, b) => a.size - b.size)[0]

    return smallest?.url ?? src
  })()

  return (
    <picture
      id={id}
      data-testid={dataTestId ?? 'product-cloudflare-picture/wrapper'}
      className={twMerge(pictureClassName, shrink ? 'shrink' : 'shrink-0')}
      {...rest}
    >
      {Object.entries(data ?? {}).map(([format, urls]) => {
        const urlsBySize = [...urls].sort((a, b) => a.size - b.size)

        if (urlsBySize.length > 1) {
          const smallest = urlsBySize[0]
          const largest = urlsBySize[urlsBySize.length - 1]

          if (!smallest || !largest) return null

          return (
            <Fragment key={format}>
              <source
                media={`(max-width: ${parseInt(desktopWidth) - 1}px)`}
                srcSet={`${smallest.url} ${smallest.size}w`}
                type={`image/${format}`}
                data-testid={`product-cloudflare-picture/source/${format}-mobile`}
              />
              <source
                media={`(min-width: ${desktopWidth})`}
                srcSet={`${largest.url} ${largest.size}w`}
                type={`image/${format}`}
                data-testid={`product-cloudflare-picture/source/${format}-desktop`}
              />
            </Fragment>
          )
        }

        const srcSet = urlsBySize.map(({ url, size }) => `${url} ${size}w`).join(', ')

        return (
          <source
            key={format}
            srcSet={srcSet}
            type={`image/${format}`}
            data-testid={`product-cloudflare-picture/source/${format}`}
          />
        )
      })}

      <img
        alt={alt || ''}
        aria-label={ariaLabel}
        src={fallbackSrc}
        className={twMerge(className, cloudflarePictureCva({ mode }))}
        data-testid="product-cloudflare-picture/image"
        loading={loadingStrategy}
        fetchPriority={fetchPriority}
        onError={() => setHasError(true)}
      />
    </picture>
  )
}
